import React, { createContext, useCallback, useContext, useState } from 'react';
import { useAuthenticatedApiContext } from '../api/useApiClient';
import { Tenant, UpdateWorkspaceResponse } from '../models/TenantModels';
import { getLoggedInUserEmail } from '../auth/oidc';
import { rememberWorkspace } from '../auth/workspaceCookie';
import { AsyncState, useAsync } from './useAsync';
import { useAsyncEffect } from './useAsyncEffect';

export interface TenantContextValue {
  loadTenant: AsyncState<void, () => Promise<void>>;
  tenant: Tenant | undefined;
  postAdminSettings: AsyncState<void, (tenantId: string, adminUsername: string, adminEmail: string) => Promise<void>>;
  updateWorkspace: AsyncState<
    UpdateWorkspaceResponse,
    (tenantId: string, workspace: string) => Promise<UpdateWorkspaceResponse>
  >;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | undefined>();
  const apiClient = useAuthenticatedApiContext();

  const loadTenant = useAsync(
    useCallback(async (): Promise<void> => {
      const loadedTenant = await apiClient.getTenant();
      setTenant(loadedTenant);
      // Remember the workspace on every successful load so the workspace-selection page can suggest it later.
      const email = getLoggedInUserEmail();
      if (email && loadedTenant.workspace) {
        rememberWorkspace({ email, workspace: loadedTenant.workspace });
      }
    }, [apiClient]),
    { autoReset: true }
  );

  useAsyncEffect(loadTenant, []);

  const postAdminSettings = useAsync(
    useCallback(
      async (tenantId: string, adminUsername: string, adminEmail: string): Promise<void> => {
        await apiClient.postAdminSettings(tenantId, adminUsername, adminEmail);
        loadTenant.reset();
        await loadTenant.execute(tenantId);
      },
      [loadTenant, apiClient]
    ),
    { autoReset: true }
  );

  const updateWorkspace = useAsync(
    useCallback(
      async (tenantId: string, workspace: string): Promise<UpdateWorkspaceResponse> => {
        const response = await apiClient.updateWorkspace(tenantId, workspace);
        loadTenant.reset();
        await loadTenant.execute();
        return response;
      },
      [loadTenant, apiClient]
    ),
    { autoReset: true }
  );

  const value: TenantContextValue = {
    loadTenant,
    tenant,
    postAdminSettings,
    updateWorkspace,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
